import { z } from 'zod'
import type { UseSeoMetaInput } from '@unhead/vue'

// This is an enum for the content types of our content pages. Some are indexes or hubs which list more content and some are standalone content.
export enum ContentTypeEnum {
  Guide = 'guide',
  Hub = 'hub',
  Index = 'index',
  Article = 'article',
  Post = 'post',
  CheatSheet = 'cheat sheet',
  HowTo = 'how-to',
}

export enum PublishedStatusEnum {
  Draft = 'draft',
  Published = 'published',
  Unpublished = 'unpublished',
}

// Static data that doesn't need to be reactive can just be stored in a simple exported object, such as this defaults object for SEO metadata. This allows us to easily import and use these defaults in any page component or utility function, such as the setSEO function which merges page frontmatter with these defaults to create the final head and SEO metadata for each page.
export const defaults: SeoMetaDefaults = {
  siteName: 'Pennock Projects',
  title: 'Pennock Projects',
  description: 'Pennock Projects is a software engineering blog about website and mobile applications, front end frameworks, backend API services, databases, and AI architecture by John Pennock',
  author: 'John Pennock',
  content_type: ContentTypeEnum.Post,
  copyright: '© 2024-2026 by John Pennock',
  creator: 'Pennock Projects',
  image2x1: '/images/PennockProjectsFB.jpg',
  image2x1Width: 1200,
  image2x1Height: 600,
  image1x1: '/images/PennockProjectsLogo.png',
  image1x1Width: 800,
  image1x1Height: 800,
  imageAlt: 'Pennock Projects Logo',
  imageRoot: '/images',
  keywords: ['pennock projects'],
  ogType: 'article',
  robots: 'index, follow',
  rootUrl: "https://pennockprojects.com",
  twitterCard: 'summary_large_image',
  twitterSiteHandle: '@PennockProjects',
  twitterCreatorHandle: '@JohnPennock',
}


// Define the schema for page frontmatter using Zod
// Note front-matter variable names **must** be snake_case in NuxtContent v3 (breaking change from v2) as they store the variables in a SQLite database for queryCollection (snake_case is a limitation of SQLite).
export const PageSchemaCustom = z
  .object({
    // SEO metadata fields - these are used to generate the SEO head metadata for each page, and can be overridden on a per-page basis in the frontmatter. If not set in the frontmatter, the defaults from the defaults object will be used.
    author: z.string().optional().nullable(),
    keywords: z.array(z.string()).optional().nullable(),

    // SEO social media - these fields are for how content is displayed in Social Media posts. The main 'title' and 'description' fields and the 'image' and 'image_alt' fields for a custom image for specific post or article.  Further the `og` fields are further custom fields for sites which support Facebook 'open graph' for social posting.  The `x` files are for twitter social posting. These fields allow us to customize how our content is displayed when shared on social media, and also allow us to set different titles, descriptions, and images for social media sharing than what is displayed on the page itself. For example, we might want a shorter title for Twitter than what is displayed on the page, or we might want to use a different image for Facebook sharing than what is displayed on the page. These fields give us that flexibility and control over how our content is shared and displayed on social media platforms.
    image: z.string().optional().nullable(),
    image_alt: z.string().nullish().transform((val) => val ?? defaults.imageAlt),
    og_title: z.string().optional().nullable(), // Open Graph title override
    og_description: z.string().optional().nullable(), // Open Graph description override
    og_image: z.string().optional().nullable(), // Open Graph image override
    og_image_alt: z.string().optional().nullable(), // Open Graph image alt override
    x_title: z.string().optional().nullable(), // Twitter title override
    x_description: z.string().optional().nullable(), // Twitter description override
    x_image: z.string().optional().nullable(), // Twitter image override
    x_image_alt: z.string().optional().nullable(), // Twitter image alt override
    x_card: z.string().optional().nullable(), // Twitter card type override
    x_creator_handle: z.string().optional().nullable(), // Twitter creator handle override

    // Content display fields - these are used to store additional metadata about the content, such as the author, editor, content type, format, guide, project, and published status. These can be used for filtering and displaying content on the site and for layout and design purposes. For example, we can use the content_type field to filter out hub and index pages from the main article listings, and we can use the guide and peer_order fields to create ordered guides that group related content together.
    content_type: z.string().optional().default(defaults.content_type),
    date_created: z.string().optional().nullable().default(''),
    date_modified: z.string().optional().nullable(),
    editor: z.string().optional().nullable(),
    guide: z.string().optional().nullable(),
    peer_order: z.number().optional().default(0),
    is_list_exclude: z.boolean().optional().default(false),
    is_manual_image: z.boolean().optional().default(false),
    is_manual_title: z.boolean().optional().default(false),
    is_toc: z.boolean().optional().default(false),
    project: z.string().optional().nullable(),
    projects: z.array(z.string()).optional().nullable(),
    published: z.enum([
      PublishedStatusEnum.Draft,
      PublishedStatusEnum.Published,
      PublishedStatusEnum.Unpublished,
    ]).optional().default(PublishedStatusEnum.Published),
    tagline: z.string().optional().nullable(),
    version: z.string().optional().nullable(),
  })
  
// Extend the base PageSchemaCustom with additional default fields for our page frontmatter
export const PageSchema = PageSchemaCustom.extend({
  path: z.string(),
  title: z.string(),
  description: z.string().optional(),
//  navigation: z.boolean().optional(),
//  id: z.string(),
//  stem: z.string(),
//  extension: z.string(),
//  meta: z.record(z.string(), z.unknown()),
})

// Infer the Page frontmatter variable object TypeScript type from the Zod schema
export type PageMatter = z.infer<typeof PageSchema>;

// Define the type for the default SEO metadata
export type SeoMetaDefaults = {
  siteName: string
  title: string
  description: string
  author: string
  content_type: ContentTypeEnum | string
  copyright: string
  creator: string
  image1x1: string
  image1x1Width: number,
  image1x1Height: number,
  image2x1: string
  image2x1Width: number,
  image2x1Height: number,
  imageAlt: string
  imageRoot: string
  keywords: string[]
  ogType: UseSeoMetaInput['ogType']
  robots: string
  rootUrl: string
  twitterCard: UseSeoMetaInput['twitterCard']
  twitterSiteHandle: string
  twitterCreatorHandle: string
}

